import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCategorias, useCrearCategoria, useEliminarCategoria } from '../../hooks/useCategorias';
import { Button } from '../../components/shared/Button';
import { Input } from '../../components/shared/Input';
import { Spinner } from '../../components/shared/Spinner';
import { TrashIcon, PlusIcon } from '../../components/shared/icons';
import { useToast } from '../../components/shared/Toast';
import { NuevaCategoria } from '../../types/catalogo';

/** BR-CAT-07/08/09: alta y baja de categorías — cada producto debe referenciar una de
 * estas (ya no es texto libre); no se puede eliminar una en uso. */
export function CategoriasAdminPage() {
  const { data: categorias, isLoading } = useCategorias();
  const crear = useCrearCategoria();
  const eliminar = useEliminarCategoria();
  const { mostrarError, mostrarExito } = useToast();
  const [mostrarForm, setMostrarForm] = useState(false);

  const { register, handleSubmit, reset } = useForm<NuevaCategoria>();

  const crearCategoria = (datos: NuevaCategoria) => {
    crear.mutate(datos, {
      onSuccess: () => {
        mostrarExito('Categoría creada');
        reset();
        setMostrarForm(false);
      },
      onError: (error) => mostrarError((error as Error).message),
    });
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center">
        <div className="text-[13px] text-admin-ink-muted">{categorias?.length ?? 0} categorías</div>
        <Button onClick={() => setMostrarForm((v) => !v)}>{mostrarForm ? 'Cancelar' : 'Nueva categoría'}</Button>
      </div>

      {mostrarForm && (
        <form
          onSubmit={handleSubmit(crearCategoria)}
          className="bg-white border border-admin-border rounded-xl p-6 flex flex-col gap-4 max-w-md"
        >
          <Input
            label="Nombre"
            placeholder="Ej. Rosarios"
            {...register('nombre', { required: true })}
          />
          <Button type="submit" disabled={crear.isPending}>
            <PlusIcon width={15} height={15} />
            {crear.isPending ? 'Creando…' : 'Crear categoría'}
          </Button>
        </form>
      )}

      {isLoading ? (
        <Spinner />
      ) : (categorias ?? []).length === 0 ? (
        <p className="text-admin-ink-muted text-sm">Todavía no hay categorías creadas.</p>
      ) : (
        <div className="bg-white border border-admin-border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[420px] border-collapse text-[13px]">
            <thead>
              <tr>
                {['Nombre', 'Identificador', ''].map((h) => (
                  <th
                    key={h}
                    className="text-left text-[11px] font-bold uppercase tracking-wide text-admin-ink-muted px-4 py-2.5 border-b border-admin-border"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categorias!.map((cat) => (
                <tr key={cat.id} data-testid={`category-row-${cat.id}`} className="border-b border-admin-border/60 last:border-0">
                  <td className="px-4 py-3.5 font-semibold">{cat.nombre}</td>
                  <td className="px-4 py-3.5 text-admin-ink-muted">{cat.id}</td>
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() =>
                        eliminar.mutate(cat.id, {
                          onSuccess: () => mostrarExito('Categoría eliminada'),
                          onError: (e) => mostrarError((e as Error).message),
                        })
                      }
                      data-testid={`category-delete-${cat.id}`}
                      aria-label={`Eliminar categoría ${cat.nombre}`}
                    >
                      <TrashIcon width={15} height={15} className="text-danger" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
